import { CollectionNameEnum, PurchaseOrderStatusEnum } from "@finway-group/shared/lib/models"
import { PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN } from "@finway-group/shared/lib/models/purchaseOrder/purchaseOrderStatus.enum"
import { RightEnum } from "@finway-group/shared/lib/models/role/rights.enum"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils/helpers"
import { Avatar, Card, Divider, Skeleton } from "antd"
import React, { Suspense, useState } from "react"
import { useTranslation } from "react-i18next"
import { generatePath, useHistory, useLocation, useParams } from "react-router-dom"

import { Loading } from "Components/loading"
import { NoSearchDataFound } from "Components/NoSearchDataFound"
import { CardTag } from "Components/tags/CardTag"
import { PurchaseOrderStatusTag } from "Features/pages/purchaseOrders/components/PurchaseOrderStatusTag"
import { PurchaseOrderFormValues } from "Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { useCostCenters } from "Shared/hooks/costCenter.hooks"
import { useLoggedInEmployeeProfile } from "Shared/queries/employee.queries"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { AuthzService } from "Shared/services"
import { isResponsibleForCostCenterOrSharedWith } from "Shared/utils/costCenter.utils"
import { getCostCentersFromSplits } from "Shared/utils/expense.utils"
import { lazily } from "Shared/utils/react.utils"
import { ExpenseRequestTypeEnum } from "@finway-group/shared/lib/models/expense/expenseRequestType.enum"
import { DeliveryNoteDrawer } from "Features/pages/deliveryNotes/components/DelieryNoteDrawer"
import { DeliveryNoteFormValues } from "Features/pages/deliveryNotes/types/deliveryNote.interfaces"
import { ExpenseCreateFormModal } from "Components/modals/expenseCreateForm.modal"
import CostCenterBudgetUsageProgressBar from "../costCenters/costCenterBudgetUsageProgressBar"
import { LineItemCardReadOnly } from "./components/LineItemCardReadOnly"

import { FileViewerDetails } from "./components/FileViewerDetails"
import { PurchaseOrderActionButtons } from "./components/PurchaseOrderActionButtons"
import { PurchaseOrderCreditCardSection } from "./components/PurchaseOrderCreditCardSection"
import { PurchaseOrderDetailsHeader } from "./components/PurchaseOrderDetailsHeader"
import { PurchaseOrderDetailsSection } from "./components/PurchaseOrderDetailsSection"
import { PurchaseOrderDrawer } from "./drawer/PurchaseOrderDrawer"
import { useDrawer } from "./hooks/useDrawer.hook"
import { useMutatePurchaseOrders } from "./hooks/usePurchaseOrders.hook"
import { useTags } from "./hooks/useTags.hook"
import { IsServiceIcon } from "./icons/IsServiceIcon"
import { ShoppingBagIcon } from "./icons/ShoppingBagIcon"
import { usePurchaseOrderQuery } from "./queries/purchaseOrder.queries"
import { ILineItem } from "./types/LineItems.interfaces"

interface PoSwitchingLocationState {
    poSwitchingSteps?: number
}

const { CommentContainer } = lazily(() => import("Components/comment/comment.container"))
const { PurchaseOrderLinksSection } = lazily(() => import("./PurchaseOrderLinksSection"))

export const PurchaseOrderDetailsPage = () => {
    const { id: purchaseOrderId } = useParams<{ id: string }>()
    const { t } = useTranslation()
    const location = useLocation<PoSwitchingLocationState>()

    const costCenters = useCostCenters()

    const { data: po, isLoading: isPurchaseOrderLoading, isError: isPurchaseOrderError } = usePurchaseOrderQuery(purchaseOrderId)

    const { attachTagToPurchaseOrder, detachTagFromPurchaseOrder } = useTags()

    const history = useHistory()
    const user = useLoggedInEmployeeProfile()
    const { deletePo } = useMutatePurchaseOrders()
    const { isDrawerOpen: isPODrawerOpen, openDrawer: openPODrawer, closeDrawer: closePODrawer } = useDrawer<PurchaseOrderFormValues>()
    const [isExpenseModalShowing, setIsExpenseModalShowing] = useState(false)
    const { isDrawerOpen: isDnDrawerOpen, closeDrawer: closeDnDrawer, openDrawer: openDnDrawer } = useDrawer<DeliveryNoteFormValues>()

    if (isPurchaseOrderLoading) {
        return <Loading />
    }

    // Early return for error state
    if (isPurchaseOrderError || !po) {
        return (
            <div className="max-w-[1200px] mx-auto p-6">
                <NoSearchDataFound />
            </div>
        )
    }

    const disabledStates = PURCHASE_ORDER_STATUSES_UPDATE_FORBIDDEN

    const isDisabledState = disabledStates.includes(po.state.currentState)

    const costCentersFromSplits = getCostCentersFromSplits(po.lineItems)
    const costCentersAllowedToView = AuthzService.isRightGrantedForLoggedInUser(RightEnum.CC1__ALL__READ)
        ? costCentersFromSplits
        : costCentersFromSplits.filter((costCenter) => isResponsibleForCostCenterOrSharedWith(costCenter, user?.id ?? "", costCenters))

    return (
        <div>
            <PurchaseOrderDetailsHeader
                title={
                    <div className="flex items-center space-x-4">
                        <h1 className="mr-6">{`${t("purchaseOrders:details.title")} #${po.purchaseOrderNumber}`}</h1>
                    </div>
                }
                purchaseOrder={po}
                tags={po.tags}
                onDelete={async () => {
                    await deletePo(po._id, () => {
                        history.push(generatePath(RoutePathEnum.PURCHASE_ORDERS))
                    })
                }}
                onBack={() => {
                    if (location.state?.poSwitchingSteps) {
                        history.go(0 - (location.state.poSwitchingSteps + 1))
                    } else {
                        history.goBack()
                    }
                }}
                onAddTag={(id: string, detach: boolean) => {
                    if (detach) {
                        detachTagFromPurchaseOrder({ purchaseOrderId, tagId: id })
                    } else {
                        attachTagToPurchaseOrder({ purchaseOrderId, tagId: id })
                    }
                }}
                onEdit={isDisabledState ? undefined : () => openPODrawer()}
                onNext={
                    po.metadata.nextPurchaseOrderId
                        ? () => {
                              history.push(generatePath(RoutePathEnum.PURCHASE_ORDER_DETAILS_PAGE, { id: po.metadata.nextPurchaseOrderId! }), {
                                  poSwitchingSteps: (location.state?.poSwitchingSteps ?? 0) + 1,
                              })
                          }
                        : undefined
                }
                onPrev={
                    po.metadata.previousPurchaseOrderId
                        ? () => {
                              history.push(generatePath(RoutePathEnum.PURCHASE_ORDER_DETAILS_PAGE, { id: po.metadata.previousPurchaseOrderId! }), {
                                  poSwitchingSteps: (location.state?.poSwitchingSteps ?? 0) + 1,
                              })
                          }
                        : undefined
                }
            />
            <div className="grid grid-cols-1 lg:grid-cols-9 gap-24 grid-rows-auto1fr">
                {/* Left column */}
                <div className="md:col-span-3 lg:col-span-6 md:row-span-2 md:order-first h-auto">
                    <Card>
                        <div className="flex items-center justify-between gap-16">
                            <div className="flex items-center gap-16">
                                <div className="flex items-center justify-center">
                                    <Avatar
                                        src={po.isService ? <IsServiceIcon /> : <ShoppingBagIcon />}
                                        size={48}
                                        className="text-indigo-600 font-bold text-2xl flex items-center rounded-full justify-center border-none  bg-blue-200 bg-opacity-10"
                                    />
                                </div>
                                <div className="flex flex-col gap-2 flex-0">
                                    <div className="flex items-center gap-4">
                                        <span className="text-lg font-bold">
                                            {po.vendor.name} <CardTag isVisible={po.createCard} />
                                        </span>
                                    </div>

                                    <div style={{ color: "#64748B" }}>
                                        <p
                                            className="text-sm sm:text-base leading-tight max-h-100 overflow-y-scroll w-full mt-4"
                                            style={{
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {po.description}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <PurchaseOrderStatusTag status={po.state.currentState} className="ml-auto" />
                        </div>
                        <div
                            style={{
                                marginTop: 24,
                                border: "1px solid #F1F5F9",
                                borderRadius: 12,
                                padding: 12,
                                background: "#fff",
                            }}
                        >
                            {po.lineItems.map((item: ILineItem, idx: number) => (
                                <div key={idx} className="mb-12 line-item-list">
                                    <LineItemCardReadOnly item={item} idx={idx} currency={po.currency} />
                                </div>
                            ))}
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 32, marginTop: 16, fontWeight: 700 }}>
                                <span>
                                    {t("purchaseOrders:details.totalNet")} {formatCurrencyNumber({ value: po.totalNetPrice, currency: po.currency })}
                                </span>
                                <span>
                                    {t("purchaseOrders:details.totalGross")} {formatCurrencyNumber({ value: po.totalGrossPrice, currency: po.currency })}
                                </span>
                            </div>
                        </div>
                    </Card>
                    <Card style={{ marginTop: 24 }}>
                        <h3 className="text-lg font-semibold mb-0">{t("purchaseOrders:linked_to_this_po")}</h3>
                        <Suspense fallback={<Skeleton active paragraph={{ rows: 3 }} />}>
                            <PurchaseOrderLinksSection purchaseOrder={po} openDnDrawer={openDnDrawer} setIsExpenseModalShowing={setIsExpenseModalShowing} />
                        </Suspense>
                    </Card>
                </div>

                {/* Right column */}
                <div className="md:col-span-2 lg:col-span-3">
                    <PurchaseOrderActionButtons purchaseOrder={po} isDisabled={isDisabledState}>
                        {po.state.currentState === PurchaseOrderStatusEnum.WAITING_FOR_APPROVAL && costCentersAllowedToView.length > 0 && (
                            <>
                                <Divider className="my-8" />
                                <CostCenterBudgetUsageProgressBar data={costCentersAllowedToView} />
                            </>
                        )}
                    </PurchaseOrderActionButtons>

                    <FileViewerDetails
                        file={{
                            name: po.purchaseOrderDocument?.name,
                            url: po.purchaseOrderDocument?.url,
                        }}
                        isDisabled={isDisabledState}
                        editRecord={() => openPODrawer()}
                        title={t("purchaseOrders:details.purchaseOrderDocument")}
                        buttonText="action:add_purchase_order"
                    />

                    <PurchaseOrderCreditCardSection purchaseOrder={po} />

                    <Suspense fallback={<Skeleton active paragraph={{ rows: 2 }} />}>
                        <PurchaseOrderDetailsSection purchaseOrder={po} />
                    </Suspense>

                    <Suspense fallback={<Skeleton active paragraph={{ rows: 3 }} />}>
                        <CommentContainer type={CollectionNameEnum.PURCHASE_ORDER} comments={po.comments} object={{ id: po._id }} />
                    </Suspense>
                </div>
            </div>
            {/* All drawers managed by the hook */}
            <PurchaseOrderDrawer open={isPODrawerOpen} onClose={closePODrawer} editMode={Boolean(po)} purchaseOrder={po} />
            <ExpenseCreateFormModal
                type={ExpenseRequestTypeEnum.INVOICE}
                isNew={true}
                isShowing={isExpenseModalShowing}
                onCancel={() => {
                    setIsExpenseModalShowing(false)
                }}
                isReimbursement={false}
                preSelectedPurchaseOrder={{ _id: po._id, purchaseOrderNumber: po.purchaseOrderNumber, alreadyLinked: false }}
            />
            <DeliveryNoteDrawer
                open={isDnDrawerOpen}
                onClose={closeDnDrawer}
                editMode={false}
                preSelectedPurchaseOrder={{ _id: po._id, purchaseOrderNumber: po.purchaseOrderNumber, alreadyLinked: false }}
            />
        </div>
    )
}
